/**
 * Mock data for No-Show Rate detail subpage — all data keyed by range tab.
 */

import type { RangeKey } from '@dataTypes/dashboard.types'

/* ─── KPIs ────────────────────────────────────────────────────────────────── */

export type NoShowRateKpiData = {
  noShowRate: string
  noShowRateGoal: string
  noShows: number
  cancellations: number
  openChairTime: string
  lostProduction: string
  rescheduleRate: string
  comparisonLabel: string
  rateDelta: string
  rateTrend: 'up' | 'down'
  noShowsDelta: string
  noShowsTrend: 'up' | 'down'
  lostProductionDelta: string
  lostProductionTrend: 'up' | 'down'
  rescheduleRateDelta: string
  rescheduleRateTrend: 'up' | 'down'
}

const NO_SHOW_KPI: Record<RangeKey, NoShowRateKpiData> = {
  '1D': {
    noShowRate: '8.3%',
    noShowRateGoal: '5%',
    noShows: 3,
    cancellations: 5,
    openChairTime: '4.5 hrs',
    lostProduction: '$1.5K',
    rescheduleRate: '63%',
    comparisonLabel: 'vs. yesterday',
    rateDelta: '6% (1 hr)',
    rateTrend: 'down',
    noShowsDelta: '12%',
    noShowsTrend: 'down',
    lostProductionDelta: '12%',
    lostProductionTrend: 'down',
    rescheduleRateDelta: '6% (1 hr)',
    rescheduleRateTrend: 'up',
  },
  '1W': {
    noShowRate: '7.8%',
    noShowRateGoal: '5%',
    noShows: 14,
    cancellations: 12,
    openChairTime: '18.5 hrs',
    lostProduction: '$7.4K',
    rescheduleRate: '65%',
    comparisonLabel: 'vs. last week',
    rateDelta: '0.8%',
    rateTrend: 'down',
    noShowsDelta: '2 appts',
    noShowsTrend: 'down',
    lostProductionDelta: '4% ($284)',
    lostProductionTrend: 'down',
    rescheduleRateDelta: '5%',
    rescheduleRateTrend: 'up',
  },
  '1M': {
    noShowRate: '7.2%',
    noShowRateGoal: '5%',
    noShows: 56,
    cancellations: 48,
    openChairTime: '72 hrs',
    lostProduction: '$28.8K',
    rescheduleRate: '68%',
    comparisonLabel: 'vs. last month',
    rateDelta: '0.6%',
    rateTrend: 'down',
    noShowsDelta: '6 appts',
    noShowsTrend: 'down',
    lostProductionDelta: '3% ($864)',
    lostProductionTrend: 'down',
    rescheduleRateDelta: '4%',
    rescheduleRateTrend: 'up',
  },
  '1Y': {
    noShowRate: '7.0%',
    noShowRateGoal: '5%',
    noShows: 672,
    cancellations: 576,
    openChairTime: '864 hrs',
    lostProduction: '$345.6K',
    rescheduleRate: '70%',
    comparisonLabel: 'vs. last year',
    rateDelta: '1.2%',
    rateTrend: 'down',
    noShowsDelta: '72 appts',
    noShowsTrend: 'down',
    lostProductionDelta: '5% ($17.3K)',
    lostProductionTrend: 'down',
    rescheduleRateDelta: '6%',
    rescheduleRateTrend: 'up',
  },
  YTD: {
    noShowRate: '7.1%',
    noShowRateGoal: '5%',
    noShows: 168,
    cancellations: 144,
    openChairTime: '216 hrs',
    lostProduction: '$86.4K',
    rescheduleRate: '69%',
    comparisonLabel: 'vs. YTD last year',
    rateDelta: '0.9%',
    rateTrend: 'down',
    noShowsDelta: '18 appts',
    noShowsTrend: 'down',
    lostProductionDelta: '4% ($3.5K)',
    lostProductionTrend: 'down',
    rescheduleRateDelta: '5%',
    rescheduleRateTrend: 'up',
  },
}

export function getNoShowRateKpi(range: RangeKey): NoShowRateKpiData {
  return NO_SHOW_KPI[range]
}

/* ─── Chart (No-shows per time slot) ─────────────────────────────────────── */

export type NoShowChartPoint = {
  label: string
  noShows: number
  cancellations: number
}

export type NoShowChartConfig = {
  data: NoShowChartPoint[]
  yAxisDomain: [number, number]
  yAxisTicks: number[]
}

const NO_SHOW_CHART: Record<RangeKey, NoShowChartConfig> = {
  '1D': {
    data: [
      { label: '8 AM', noShows: 4, cancellations: 4 },
      { label: '9 AM', noShows: 5, cancellations: 4 },
      { label: '10 AM', noShows: 4, cancellations: 3 },
      { label: '11 AM', noShows: 4, cancellations: 3 },
      { label: '12 PM', noShows: 2, cancellations: 3 },
      { label: '1 PM', noShows: 1, cancellations: 4 },
      { label: '2 PM', noShows: 2, cancellations: 5 },
      { label: '3 PM', noShows: 2, cancellations: 4 },
      { label: '4 PM', noShows: 3, cancellations: 3 },
      { label: '5 PM', noShows: 5, cancellations: 4 },
    ],
    yAxisDomain: [0, 10],
    yAxisTicks: [0, 2, 4, 6, 8, 10],
  },
  '1W': {
    data: [
      { label: 'Mon', noShows: 3, cancellations: 2 },
      { label: 'Tue', noShows: 4, cancellations: 3 },
      { label: 'Wed', noShows: 2, cancellations: 3 },
      { label: 'Thu', noShows: 3, cancellations: 2 },
      { label: 'Fri', noShows: 2, cancellations: 2 },
    ],
    yAxisDomain: [0, 6],
    yAxisTicks: [0, 2, 4, 6],
  },
  '1M': {
    data: [
      { label: 'Wk 1', noShows: 14, cancellations: 12 },
      { label: 'Wk 2', noShows: 16, cancellations: 14 },
      { label: 'Wk 3', noShows: 12, cancellations: 10 },
      { label: 'Wk 4', noShows: 14, cancellations: 12 },
    ],
    yAxisDomain: [0, 20],
    yAxisTicks: [0, 5, 10, 15, 20],
  },
  '1Y': {
    data: [
      { label: 'Apr', noShows: 52, cancellations: 44 },
      { label: 'May', noShows: 58, cancellations: 50 },
      { label: 'Jun', noShows: 54, cancellations: 46 },
      { label: 'Jul', noShows: 60, cancellations: 52 },
      { label: 'Aug', noShows: 56, cancellations: 48 },
      { label: 'Sep', noShows: 50, cancellations: 44 },
      { label: 'Oct', noShows: 54, cancellations: 46 },
      { label: 'Nov', noShows: 58, cancellations: 50 },
      { label: 'Dec', noShows: 52, cancellations: 44 },
      { label: 'Jan', noShows: 56, cancellations: 48 },
      { label: 'Feb', noShows: 58, cancellations: 50 },
      { label: 'Mar', noShows: 64, cancellations: 54 },
    ],
    yAxisDomain: [0, 80],
    yAxisTicks: [0, 20, 40, 60, 80],
  },
  YTD: {
    data: [
      { label: 'Jan', noShows: 56, cancellations: 48 },
      { label: 'Feb', noShows: 58, cancellations: 50 },
      { label: 'Mar', noShows: 54, cancellations: 46 },
    ],
    yAxisDomain: [0, 80],
    yAxisTicks: [0, 20, 40, 60, 80],
  },
}

export function getNoShowChartConfig(range: RangeKey): NoShowChartConfig {
  return NO_SHOW_CHART[range]
}

/* ─── By provider ─────────────────────────────────────────────────────────── */

export type NoShowByProviderRow = {
  id: string
  provider: string
  scheduled: number
  noShows: number
  cancellations: number
  noShowRate: string
  lostProduction: string
  rescheduled: number
}

const BY_PROVIDER: Record<RangeKey, NoShowByProviderRow[]> = {
  '1D': [
    {
      id: '1',
      provider: 'Dr. Toby Nolan',
      scheduled: 14,
      noShows: 1,
      cancellations: 1,
      noShowRate: '7.1%',
      lostProduction: '$420',
      rescheduled: 1,
    },
    {
      id: '2',
      provider: 'Dr. Clara Bennett',
      scheduled: 12,
      noShows: 1,
      cancellations: 1,
      noShowRate: '8.3%',
      lostProduction: '$380',
      rescheduled: 1,
    },
    {
      id: '3',
      provider: 'Dr. Juan Ramirez',
      scheduled: 11,
      noShows: 0,
      cancellations: 1,
      noShowRate: '0%',
      lostProduction: '$0',
      rescheduled: 1,
    },
    {
      id: '4',
      provider: 'Dr. Lisa Chen',
      scheduled: 10,
      noShows: 1,
      cancellations: 1,
      noShowRate: '10%',
      lostProduction: '$340',
      rescheduled: 0,
    },
    {
      id: '5',
      provider: 'Dr. Marcus Alvarez',
      scheduled: 9,
      noShows: 0,
      cancellations: 1,
      noShowRate: '0%',
      lostProduction: '$0',
      rescheduled: 1,
    },
    {
      id: '6',
      provider: 'Dr. Priya Nair',
      scheduled: 8,
      noShows: 0,
      cancellations: 0,
      noShowRate: '0%',
      lostProduction: '$0',
      rescheduled: 0,
    },
    {
      id: '7',
      provider: 'Dr. Samuel Okoro',
      scheduled: 8,
      noShows: 0,
      cancellations: 1,
      noShowRate: '0%',
      lostProduction: '$0',
      rescheduled: 0,
    },
    {
      id: '8',
      provider: 'Dr. Fiona Gallagher',
      scheduled: 7,
      noShows: 0,
      cancellations: 0,
      noShowRate: '0%',
      lostProduction: '$0',
      rescheduled: 0,
    },
    {
      id: '9',
      provider: 'Dr. Kenji Tanaka',
      scheduled: 6,
      noShows: 0,
      cancellations: 0,
      noShowRate: '0%',
      lostProduction: '$0',
      rescheduled: 0,
    },
    {
      id: '10',
      provider: 'Dr. Amina El-Sayed',
      scheduled: 6,
      noShows: 0,
      cancellations: 0,
      noShowRate: '0%',
      lostProduction: '$0',
      rescheduled: 0,
    },
  ],
  '1W': [
    {
      id: '1',
      provider: 'Dr. Alfred Weston',
      scheduled: 58,
      noShows: 3,
      cancellations: 2,
      noShowRate: '5.2%',
      lostProduction: '$1,680',
      rescheduled: 3,
    },
    {
      id: '2',
      provider: 'Dr. Clara Hughes',
      scheduled: 54,
      noShows: 4,
      cancellations: 3,
      noShowRate: '7.4%',
      lostProduction: '$1,960',
      rescheduled: 4,
    },
    {
      id: '3',
      provider: 'Dr. James Smith',
      scheduled: 48,
      noShows: 4,
      cancellations: 3,
      noShowRate: '8.3%',
      lostProduction: '$1,960',
      rescheduled: 3,
    },
    {
      id: '4',
      provider: 'RDH Sarah Lee',
      scheduled: 48,
      noShows: 2,
      cancellations: 2,
      noShowRate: '4.2%',
      lostProduction: '$980',
      rescheduled: 2,
    },
    {
      id: '5',
      provider: 'RDH David Wilson',
      scheduled: 45,
      noShows: 1,
      cancellations: 2,
      noShowRate: '2.2%',
      lostProduction: '$420',
      rescheduled: 2,
    },
  ],
  '1M': [
    {
      id: '1',
      provider: 'Dr. Alfred Weston',
      scheduled: 236,
      noShows: 12,
      cancellations: 10,
      noShowRate: '5.1%',
      lostProduction: '$6,720',
      rescheduled: 10,
    },
    {
      id: '2',
      provider: 'Dr. Clara Hughes',
      scheduled: 216,
      noShows: 16,
      cancellations: 12,
      noShowRate: '7.4%',
      lostProduction: '$8,960',
      rescheduled: 14,
    },
    {
      id: '3',
      provider: 'Dr. James Smith',
      scheduled: 190,
      noShows: 14,
      cancellations: 12,
      noShowRate: '7.4%',
      lostProduction: '$7,840',
      rescheduled: 12,
    },
    {
      id: '4',
      provider: 'RDH Sarah Lee',
      scheduled: 193,
      noShows: 8,
      cancellations: 8,
      noShowRate: '4.1%',
      lostProduction: '$4,480',
      rescheduled: 8,
    },
    {
      id: '5',
      provider: 'RDH David Wilson',
      scheduled: 191,
      noShows: 6,
      cancellations: 6,
      noShowRate: '3.1%',
      lostProduction: '$2,520',
      rescheduled: 6,
    },
  ],
  '1Y': [
    {
      id: '1',
      provider: 'Dr. Alfred Weston',
      scheduled: 2832,
      noShows: 144,
      cancellations: 120,
      noShowRate: '5.1%',
      lostProduction: '$80,640',
      rescheduled: 120,
    },
    {
      id: '2',
      provider: 'Dr. Clara Hughes',
      scheduled: 2592,
      noShows: 192,
      cancellations: 144,
      noShowRate: '7.4%',
      lostProduction: '$107,520',
      rescheduled: 168,
    },
    {
      id: '3',
      provider: 'Dr. James Smith',
      scheduled: 2280,
      noShows: 168,
      cancellations: 144,
      noShowRate: '7.4%',
      lostProduction: '$94,080',
      rescheduled: 144,
    },
    {
      id: '4',
      provider: 'RDH Sarah Lee',
      scheduled: 2316,
      noShows: 96,
      cancellations: 96,
      noShowRate: '4.1%',
      lostProduction: '$53,760',
      rescheduled: 96,
    },
    {
      id: '5',
      provider: 'RDH David Wilson',
      scheduled: 2292,
      noShows: 72,
      cancellations: 72,
      noShowRate: '3.1%',
      lostProduction: '$30,240',
      rescheduled: 72,
    },
  ],
  YTD: [
    {
      id: '1',
      provider: 'Dr. Alfred Weston',
      scheduled: 708,
      noShows: 36,
      cancellations: 30,
      noShowRate: '5.1%',
      lostProduction: '$20,160',
      rescheduled: 30,
    },
    {
      id: '2',
      provider: 'Dr. Clara Hughes',
      scheduled: 648,
      noShows: 48,
      cancellations: 36,
      noShowRate: '7.4%',
      lostProduction: '$26,880',
      rescheduled: 42,
    },
    {
      id: '3',
      provider: 'Dr. James Smith',
      scheduled: 570,
      noShows: 42,
      cancellations: 36,
      noShowRate: '7.4%',
      lostProduction: '$23,520',
      rescheduled: 36,
    },
    {
      id: '4',
      provider: 'RDH Sarah Lee',
      scheduled: 579,
      noShows: 24,
      cancellations: 24,
      noShowRate: '4.1%',
      lostProduction: '$13,440',
      rescheduled: 24,
    },
    {
      id: '5',
      provider: 'RDH David Wilson',
      scheduled: 573,
      noShows: 18,
      cancellations: 18,
      noShowRate: '3.1%',
      lostProduction: '$7,560',
      rescheduled: 18,
    },
  ],
}

export function getNoShowByProviderRows(
  range: RangeKey,
): NoShowByProviderRow[] {
  return BY_PROVIDER[range]
}

/* ─── By time period ──────────────────────────────────────────────────────── */

export type NoShowByTimePeriodRow = {
  id: string
  timePeriod: string
  scheduled: number
  noShows: number
  cancellations: number
  noShowRate: string
  lostProduction: string
  rescheduled: number
}

const BY_TIME_PERIOD: Record<RangeKey, NoShowByTimePeriodRow[]> = {
  '1D': [
    {
      id: '1',
      timePeriod: 'Today',
      scheduled: 48,
      noShows: 3,
      cancellations: 5,
      noShowRate: '8.3%',
      lostProduction: '$1,500',
      rescheduled: 2,
    },
    {
      id: '2',
      timePeriod: 'Yesterday',
      scheduled: 46,
      noShows: 4,
      cancellations: 4,
      noShowRate: '8.7%',
      lostProduction: '$1,680',
      rescheduled: 3,
    },
    {
      id: '3',
      timePeriod: 'Last week (same day)',
      scheduled: 44,
      noShows: 5,
      cancellations: 3,
      noShowRate: '11.4%',
      lostProduction: '$2,100',
      rescheduled: 3,
    },
  ],
  '1W': [
    {
      id: '1',
      timePeriod: 'This week',
      scheduled: 253,
      noShows: 14,
      cancellations: 12,
      noShowRate: '7.8%',
      lostProduction: '$7,400',
      rescheduled: 9,
    },
    {
      id: '2',
      timePeriod: 'Last week',
      scheduled: 248,
      noShows: 16,
      cancellations: 14,
      noShowRate: '8.6%',
      lostProduction: '$8,120',
      rescheduled: 10,
    },
    {
      id: '3',
      timePeriod: 'Last month same week',
      scheduled: 242,
      noShows: 18,
      cancellations: 14,
      noShowRate: '9.2%',
      lostProduction: '$9,240',
      rescheduled: 9,
    },
  ],
  '1M': [
    {
      id: '1',
      timePeriod: 'This month',
      scheduled: 1026,
      noShows: 56,
      cancellations: 48,
      noShowRate: '7.2%',
      lostProduction: '$28,800',
      rescheduled: 38,
    },
    {
      id: '2',
      timePeriod: 'Last month',
      scheduled: 1008,
      noShows: 62,
      cancellations: 52,
      noShowRate: '7.9%',
      lostProduction: '$31,860',
      rescheduled: 40,
    },
    {
      id: '3',
      timePeriod: 'Last quarter',
      scheduled: 3024,
      noShows: 186,
      cancellations: 156,
      noShowRate: '8.1%',
      lostProduction: '$95,580',
      rescheduled: 118,
    },
  ],
  '1Y': [
    {
      id: '1',
      timePeriod: 'This year',
      scheduled: 12312,
      noShows: 672,
      cancellations: 576,
      noShowRate: '7.0%',
      lostProduction: '$345,600',
      rescheduled: 470,
    },
    {
      id: '2',
      timePeriod: 'Last year',
      scheduled: 11988,
      noShows: 744,
      cancellations: 624,
      noShowRate: '8.2%',
      lostProduction: '$382,320',
      rescheduled: 494,
    },
    {
      id: '3',
      timePeriod: '2 years ago',
      scheduled: 11664,
      noShows: 840,
      cancellations: 684,
      noShowRate: '9.6%',
      lostProduction: '$431,640',
      rescheduled: 504,
    },
  ],
  YTD: [
    {
      id: '1',
      timePeriod: 'Jan 2026',
      scheduled: 3080,
      noShows: 56,
      cancellations: 48,
      noShowRate: '7.1%',
      lostProduction: '$28,560',
      rescheduled: 38,
    },
    {
      id: '2',
      timePeriod: 'Feb 2026',
      scheduled: 3024,
      noShows: 58,
      cancellations: 50,
      noShowRate: '7.4%',
      lostProduction: '$29,580',
      rescheduled: 40,
    },
    {
      id: '3',
      timePeriod: 'Mar 2026',
      scheduled: 3108,
      noShows: 54,
      cancellations: 46,
      noShowRate: '6.8%',
      lostProduction: '$27,540',
      rescheduled: 38,
    },
  ],
}

export function getNoShowByTimePeriodRows(
  range: RangeKey,
): NoShowByTimePeriodRow[] {
  return BY_TIME_PERIOD[range]
}
